/**
 * Pages/Panel/PreNomina/SobreRecibo/ModalDetalleConcepto.js
 *
 * Detalle de un concepto del sobre-recibo: se abre con doble clic sobre su nombre.
 *
 * Contesta dos preguntas distintas:
 *
 *   1. "¿Cuánto calculó y de dónde salió cada importe?" — la tabla de arriba, con los
 *      cinco campos del concepto (el importe total y los cuatro importes de gravado /
 *      exento), su valor calculado, el capturado a mano si lo hay, y cuál de los dos
 *      terminó usándose.
 *
 *   2. "¿Cómo funcionó mi fórmula?" — el botón de cada renglón. Ahí se ve la fórmula,
 *      qué valor tomó cada variable que menciona, la misma fórmula reescrita con esos
 *      números, y el resultado de cada sub-expresión de la más interna a la más
 *      externa. Es el sustituto de tener que adivinar por qué un concepto salió en 0.
 *
 * La descripción de cada variable se toma del MISMO catálogo que ve el usuario en la
 * Guía de Fórmulas, para no mantener dos textos distintos para lo mismo. Una variable
 * sin descripción no es un error: significa que el catálogo se quedó atrás del motor.
 */

import React, { useState, useMemo } from 'react'
import { Alert, Tag, Tooltip } from 'antd'
import { ReloadOutlined, FunctionOutlined, DownOutlined } from '@ant-design/icons'
import { ModalItem, ButtonItem } from '../../../../Components/Items'
import { CATEGORIAS, FUNCIONES } from '../../Conceptos/GuiaFormulas/catalogoFormulas'

// ─── Estilos (los del sobre-recibo) ──────────────────────────────────────────
const TH = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '5px 8px', fontSize: 11, fontWeight: 600,
  color: '#555', whiteSpace: 'nowrap', textAlign: 'center',
}
const TD      = { border: '1px solid #d0d0d0', padding: '4px 8px', fontSize: 12, verticalAlign: 'top' }
const TD_NUM  = { ...TD, textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }
const MONO    = { fontFamily: 'monospace', fontSize: 12 }
const NOTA    = { fontSize: 11, color: '#888', fontStyle: 'italic', lineHeight: 1.45 }

const fmt = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—'

const TIPO_TXT = { 1: 'Percepción', 2: 'Deducción', 3: 'Obligación' }

// Etiqueta de cada campo. Los nombres reales los pone el usuario en el catálogo
// (nombreImporte1..4); estos son sólo el respaldo cuando los dejó vacíos.
const ETIQUETA_CAMPO = {
  importeTotal: 'Importe total',
  importe1: 'Importe 1',
  importe2: 'Importe 2',
  importe3: 'Importe 3',
  importe4: 'Importe 4',
}
// Para qué sirve cada importe. Es una convención fija del motor, no algo configurable:
// el gravado y el exento de ISR e IMSS salen de estos cuatro campos.
const USO_CAMPO = {
  importe1: 'Gravado ISR',
  importe2: 'Exento ISR',
  importe3: 'Gravado IMSS',
  importe4: 'Exento IMSS',
}

// ─── Índice de descripciones del catálogo de fórmulas ────────────────────────
const descripcionesVariables = () => {
  const m = new Map()
  for (const cat of CATEGORIAS || []) {
    for (const v of cat.variables || []) if (!m.has(v.nombre)) m.set(v.nombre, v.desc)
  }
  return m
}
const descripcionesFunciones = () => {
  const m = new Map()
  for (const f of FUNCIONES || []) {
    const nombre = String(f.firma || '').split('(')[0].trim()
    if (nombre && !m.has(nombre)) m.set(nombre, f.desc)
  }
  return m
}

// ─── Panel de desglose de una fórmula ────────────────────────────────────────
function DesgloseFormula({ traza, descVars, descFns }) {
  if (!traza) {
    return (
      <div style={NOTA}>
        Este campo no se evaluó: el importe está fijado a mano con cálculo inverso, así que
        la fórmula no se usa.
      </div>
    )
  }

  return (
    <div style={{ background: '#fbfcfd', border: '1px solid #e8eef5', borderRadius: 6, padding: '10px 12px' }}>
      {traza.error && <Alert type="warning" showIcon style={{ marginBottom: 10 }} message={traza.error} />}

      {traza.desconocidos?.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message={
            traza.desconocidos.length === 1
              ? 'Un símbolo de la fórmula no tenía valor y se tomó como 0'
              : `${traza.desconocidos.length} símbolos de la fórmula no tenían valor y se tomaron como 0`
          }
          description={
            <div style={{ fontSize: 12 }}>
              {traza.desconocidos.map(d => (
                <div key={d.nombre} style={{ marginTop: 3 }}>
                  <span style={{ ...MONO, fontWeight: 600 }}>{d.nombre}</span>
                  {' — '}{d.motivo}
                </div>
              ))}
            </div>
          }
        />
      )}

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>La fórmula</div>
        <div style={{ ...MONO, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, padding: '5px 8px' }}>
          {traza.formula}
        </div>
      </div>

      {traza.formulaConValores && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>La misma fórmula con los valores de este empleado</div>
          <div style={{ ...MONO, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, padding: '5px 8px', color: '#1A3A5C' }}>
            {traza.formulaConValores}
          </div>
        </div>
      )}

      {traza.variables.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Qué valor tomó cada variable</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {traza.variables.map(v => (
                <tr key={v.nombre}>
                  <td style={{ ...TD, ...MONO, width: 250, background: v.desconocido ? '#fffbe6' : '#fff' }}>
                    {v.nombre}
                    {v.desconocido && (
                      <Tag color="warning" style={{ marginLeft: 6, fontSize: 10, lineHeight: '16px' }}>
                        sin valor
                      </Tag>
                    )}
                  </td>
                  <td style={{ ...TD_NUM, width: 120, background: v.desconocido ? '#fffbe6' : '#fff' }}>{fmt(v.valor)}</td>
                  <td style={{ ...TD, ...NOTA, background: v.desconocido ? '#fffbe6' : '#fff' }}>
                    {v.desconocido
                      ? 'No existe en el scope: se tomó 0 (ver el aviso de arriba).'
                      : (descVars.get(v.nombre) || 'Sin descripción en la Guía de Fórmulas.')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {traza.funciones.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>Qué devolvió cada función</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {traza.funciones.map((f, i) => (
                <tr key={i}>
                  <td style={{ ...TD, ...MONO, width: 250, background: '#fff' }}>
                    {f.nombre}({f.args.join(', ')})
                    {descFns.get(f.nombre) && (
                      <div style={NOTA}>{descFns.get(f.nombre)}</div>
                    )}
                  </td>
                  <td style={{ ...TD_NUM, width: 120, background: '#fff' }}>{f.resultado}</td>
                  <td style={{ ...TD, background: '#fff' }} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {traza.pasos.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>
            Paso a paso, de adentro hacia afuera
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {traza.pasos.map((p, i) => {
                const ultimo = i === traza.pasos.length - 1
                return (
                  <tr key={i} style={ultimo ? { background: '#f0f7ff' } : undefined}>
                    <td style={{ ...TD, ...MONO, background: ultimo ? undefined : '#fff', fontWeight: ultimo ? 600 : 400 }}>
                      {p.expresion}
                    </td>
                    <td style={{ ...TD_NUM, width: 140, background: ultimo ? undefined : '#fff', fontWeight: ultimo ? 700 : 400 }}>
                      {p.valor === null ? 'no se pudo evaluar' : fmt(p.valor)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #dfe6ee', display: 'flex', justifyContent: 'space-between' }}>
        <b style={{ fontSize: 12 }}>Resultado de la fórmula</b>
        <b style={{ ...MONO, fontSize: 13 }}>{fmt(traza.resultado)}</b>
      </div>
    </div>
  )
}

// ─── Modal ───────────────────────────────────────────────────────────────────
export default function ModalDetalleConcepto({
  open, onClose, concepto, loading, error, onReintentar, avisoRecalculo, estatusLinea,
}) {
  const [abierto, setAbierto] = useState(null) // campo cuyo desglose está desplegado
  const descVars = useMemo(descripcionesVariables, [])
  const descFns  = useMemo(descripcionesFunciones, [])

  const trazaDe = (campo) =>
    (concepto?.trazas || []).find(t => t.campo === campo)?.traza ?? null

  const difEsteConcepto = (avisoRecalculo || [])
    .find(d => d.conceptoNominaId === concepto?.conceptoNominaId)

  return (
    <ModalItem
      title={
        concepto
          ? <span><span style={{ ...MONO, color: '#999', marginRight: 8 }}>{concepto.serieNumero}</span>{concepto.descripcion}</span>
          : 'Detalle del concepto'
      }
      visible={open}
      onCancel={onClose}
      footer={null}
      width={980}
      paddingTop
      spinning={!!loading}
    >
      {error && (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }}
          message="No se pudo obtener el detalle" description={error}
          action={<ButtonItem size="small" onClick={onReintentar} icon={<ReloadOutlined />}>Reintentar</ButtonItem>}
        />
      )}

      {!concepto && !loading && !error && (
        <div style={{ ...NOTA, padding: '24px 0', textAlign: 'center' }}>Sin datos que mostrar.</div>
      )}

      {concepto && (
        <>
          <div style={{ marginBottom: 12 }}>
            <Tag>{TIPO_TXT[concepto.tipo] || 'Concepto'}</Tag>
            {concepto.noSuma && (
              <Tooltip title="Informativo: no suma al total de la nómina">
                <Tag color="default">No suma</Tag>
              </Tooltip>
            )}
            {concepto.agregadoManual && <Tag color="purple">Agregado a mano</Tag>}
            {concepto.calcularInverso && <Tag color="orange">Cálculo inverso</Tag>}
          </div>

          {concepto.calcularInverso && (
            <Alert
              type="info" showIcon style={{ marginBottom: 12 }}
              message="Este concepto tiene el importe fijado a mano (cálculo inverso)"
              description="El importe total es el que se capturó, no el de la fórmula. Los demás conceptos que lo referencian usan ese mismo número."
            />
          )}

          {/* Días capturados a mano en el Valor (ver Utils/valorManualNomina.utils.js del backend). */}
          {concepto.origenImporte === 'diasPorSalario' && (
            <Alert
              type="info" showIcon style={{ marginBottom: 12 }}
              message="El importe sale de los días capturados a mano"
              description={
                <span style={{ fontSize: 12 }}>
                  {fmt(concepto.valorUsado)} días × salario diario {fmt(concepto.salarioDiarioUsado)}.
                  {' '}La fórmula del concepto no se usó. Si el salario cambió dentro del periodo, todos
                  los días se pagan con el salario vigente.
                </span>
              }
            />
          )}
          {concepto.origenImporte === 'diasReemplazan' && (
            <Alert
              type="info" showIcon style={{ marginBottom: 12 }}
              message="Los días capturados a mano reemplazan a los del módulo de Vacaciones"
              description="La fórmula se evaluó con los días del Valor en lugar de los del periodo; no se suman."
            />
          )}

          {difEsteConcepto && (
            <Alert
              type="warning" showIcon style={{ marginBottom: 12 }}
              message="Lo que se explica aquí no es exactamente lo que salió en el recibo"
              description={
                <span style={{ fontSize: 12 }}>
                  En el recibo este concepto vale <b>{fmt(difEsteConcepto.guardado)}</b>, y al volver a
                  correr el cálculo con los catálogos de hoy da <b>{fmt(difEsteConcepto.recalculado)}</b>.
                  {' '}{estatusLinea === 'timbrada' || estatusLinea === 'historica'
                    ? 'Esta línea ya está cerrada: al empleado se le pagó lo que está guardado.'
                    : 'Vuelve a calcular la nómina para que coincidan.'}
                </span>
              }
            />
          )}

          {/* ── Los importes ────────────────────────────────────────────── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
            <thead>
              <tr>
                <th style={{ ...TH, textAlign: 'left' }}>Campo</th>
                <th style={{ ...TH, textAlign: 'left' }}>Fórmula</th>
                <th style={TH}>Calculado</th>
                <th style={TH}>A mano</th>
                <th style={TH}>Se usó</th>
                <th style={TH} />
              </tr>
            </thead>
            <tbody>
              {/* El "valor" es la cantidad de la percepción (días, horas, piezas). No
                  tiene fórmula: lo trae un movimiento o lo capturó el usuario. */}
              {concepto.tipo === 1 && (
                <tr>
                  <td style={TD}>
                    <b>Valor</b>
                    <div style={NOTA}>La cantidad del renglón: días, horas o piezas. No sale de una fórmula.</div>
                  </td>
                  <td style={TD} />
                  <td style={TD_NUM}>{fmt(concepto.valor)}</td>
                  <td style={TD_NUM}>{concepto.valorManual === null ? '—' : fmt(concepto.valorManual)}</td>
                  <td style={{ ...TD_NUM, fontWeight: 700 }}>{fmt(concepto.valorUsado)}</td>
                  <td style={TD} />
                </tr>
              )}

              {concepto.campos.map(c => {
                const sinFormula = !c.formula
                const desplegado = abierto === c.campo
                return (
                  <React.Fragment key={c.campo}>
                    <tr style={sinFormula ? { color: '#bbb' } : undefined}>
                      <td style={TD}>
                        <b>{c.nombre || ETIQUETA_CAMPO[c.campo]}</b>
                        <div style={NOTA}>
                          {ETIQUETA_CAMPO[c.campo]}
                          {USO_CAMPO[c.campo] ? ` · el motor lo usa como ${USO_CAMPO[c.campo]}` : ''}
                        </div>
                      </td>
                      <td style={{ ...TD, ...MONO, fontSize: 11, color: sinFormula ? '#ccc' : '#666' }}>
                        {c.formula || '(sin fórmula)'}
                      </td>
                      <td style={TD_NUM}>{fmt(c.calculado)}</td>
                      <td style={TD_NUM}>
                        {c.manual === null ? '—' : <b style={{ color: '#1677ff' }}>{fmt(c.manual)}</b>}
                      </td>
                      <td style={{ ...TD_NUM, fontWeight: 700 }}>{fmt(c.usado)}</td>
                      <td style={{ ...TD, textAlign: 'center', width: 150 }}>
                        {!sinFormula && (
                          // `lessTime`: ButtonItem se auto-bloquea 1 s después de cada
                          // clic para evitar doble envío. En un simple abrir/cerrar eso
                          // se siente como que la pantalla se trabó.
                          <ButtonItem
                            size="small"
                            lessTime
                            icon={desplegado ? <DownOutlined /> : <FunctionOutlined />}
                            onClick={() => setAbierto(desplegado ? null : c.campo)}
                          >
                            {desplegado ? 'Ocultar' : 'Ver desglose'}
                          </ButtonItem>
                        )}
                      </td>
                    </tr>
                    {desplegado && (
                      <tr>
                        <td style={{ ...TD, padding: 10, background: '#f7f9fb' }} colSpan={6}>
                          <DesgloseFormula traza={trazaDe(c.campo)} descVars={descVars} descFns={descFns} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>

          <div style={NOTA}>
            Cuando hay un importe capturado a mano, ése gana sobre el de la fórmula: es el que
            sale en el recibo y el que ven los demás conceptos que referencian a éste.
          </div>
        </>
      )}
    </ModalItem>
  )
}
